import {type GridColDef, type GridValidRowModel} from '@mui/x-data-grid-pro';
import {MOON_250} from '@wandb/weave/common/css/color.styles';
import {Icon} from '@wandb/weave/components/Icon';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {Component, type ReactNode} from 'react';

interface CellErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface CellErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component specifically designed for data grid cells.
 * Prevents errors in individual cells from crashing the entire page.
 * Shows a subtle error indicator with tooltip when an error occurs.
 */
class CellErrorBoundary extends Component<
  CellErrorBoundaryProps,
  CellErrorBoundaryState
> {
  constructor(props: CellErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError(error: Error): CellErrorBoundaryState {
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging
    console.error('Cell rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error display: subtle icon with tooltip
      return <CellErrorFallback />;
    }

    return this.props.children;
  }
}

const CellErrorFallback = () => {
  return (
    <TailwindContents>
      <div className="flex items-center gap-4" style={{color: MOON_250}}>
        <Icon
          name="warning"
          style={{
            width: '16px',
            height: '16px',
          }}
        />
        Render error
      </div>
    </TailwindContents>
  );
};

export const protectColumnsFromErrors = <T extends GridValidRowModel>(
  unprotectedColumnDefs: readonly GridColDef<T>[]
): GridColDef<T>[] => {
  return unprotectedColumnDefs.map(colDef => {
    if (!colDef.renderCell) {
      return colDef;
    }
    const renderCell = colDef.renderCell;
    return {
      ...colDef,
      renderCell: params => (
        <CellErrorBoundary>{renderCell(params)}</CellErrorBoundary>
      ),
    };
  });
};
